import { motion } from "framer-motion";
import { ShieldCheck, Mail } from "lucide-react";
import { LegalLayout, LegalSection, LegalList } from "../components/LegalLayout";

export const RefundPolicy = () => (
  <LegalLayout overline="Legal · BREELINK" title="Refund Policy" titleTestId="refund-title">
    <p className="mt-8 text-base leading-relaxed text-inkMuted">
      This Refund Policy governs the conditions under which refunds are issued for products and
      services purchased through the Breelnk platform. By purchasing any paid product or service from
      Breelnk, you acknowledge and agree to the terms outlined below.
    </p>

    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.18 }}
      data-testid="refund-callout"
      className="mt-10 rounded-3xl border border-line bg-surface p-7 shadow-soft md:p-9"
    >
      <div className="flex items-center gap-3">
        <span className="grid h-11 w-11 place-items-center rounded-2xl bg-lime text-limeText">
          <ShieldCheck size={22} />
        </span>
        <h2 className="font-display text-xl font-semibold tracking-tight text-ink md:text-2xl">
          14-Day Unconditional Refund Right
        </h2>
      </div>
      <p className="mt-5 text-sm leading-relaxed text-inkMuted md:text-base">
        Breelnk provides a 14-day unconditional refund right for all purchases made through our
        website. Customers may request a full refund within fourteen (14) calendar days from the date
        of purchase, without the need to provide any justification. This refund right applies without
        any conditions, limitations, or exceptions.
      </p>
      <p className="mt-4 text-sm font-semibold text-ink md:text-base">
        All refund requests submitted within this period will be approved.
      </p>
    </motion.div>

    <LegalSection title="Refund Eligibility" testid="refund-section-eligibility">
      <LegalList
        items={[
          "Refunds are available provided that the request is submitted within the 14-day refund window.",
          "Refund eligibility is not dependent on account activity, feature usage, subscription status, or service consumption.",
          "No exclusions apply during the refund period.",
        ]}
      />
    </LegalSection>

    <LegalSection title="Refund Request Procedure" testid="refund-section-procedure">
      <p>To initiate a refund request, customers must contact Breelnk through one of the following channels:</p>
      <LegalList
        items={[
          "Replying directly to the payment confirmation email received at the time of purchase.",
          "Contacting our support team and providing the relevant transaction or order reference.",
        ]}
      />
      <p>Refunds will be issued using the original payment method used during checkout.</p>
    </LegalSection>

    <LegalSection title="Refund Processing" testid="refund-section-processing">
      <p>
        Once a refund request has been received, it will be processed within a reasonable timeframe.
        The time required for the refunded amount to appear in the customer's account may vary
        depending on the payment provider or financial institution.
      </p>
    </LegalSection>

    <LegalSection title="Post-Refund Period" testid="refund-section-post">
      <p>
        After the expiration of the 14-day refund period, all purchases are deemed final and no
        refunds will be granted.
      </p>
    </LegalSection>

    <LegalSection title="Scope of Application" testid="refund-section-scope">
      <p>This Refund Policy applies to all Breelnk offerings, including but not limited to:</p>
      <LegalList
        items={[
          "Subscription plans",
          "Digital products and services",
          "Software-as-a-Service (SaaS) features",
          "Add-ons, upgrades, and paid functionality",
        ]}
      />
    </LegalSection>

    <LegalSection title="Contact Information" testid="refund-section-contact-info">
      <p>
        For any questions or inquiries related to this Refund Policy, customers may contact Breelnk
        through the official contact channels provided on the website.
      </p>
    </LegalSection>

    <div className="mt-14 rounded-3xl border border-line bg-surfaceAlt p-7 md:p-9" data-testid="refund-contact">
      <h2 className="font-display text-xl font-semibold tracking-tight text-ink md:text-2xl">Contact Us</h2>
      <p className="mt-4 text-sm leading-relaxed text-inkMuted md:text-base">
        For questions or concerns regarding our Refund Policy, please contact:
      </p>
      <a
        href="mailto:support@breelnk.click"
        data-testid="refund-contact-email"
        className="group mt-5 inline-flex items-center gap-2.5 rounded-full bg-lime px-6 py-3.5 text-sm font-semibold text-limeText transition-transform hover:-translate-y-0.5"
      >
        <Mail size={16} />
        support@breelnk.click
      </a>
      <p className="mt-6 text-sm text-inkMuted">
        We appreciate your understanding and cooperation. Thank you for choosing Breelnk.click.
      </p>
    </div>
  </LegalLayout>
);
